const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.alexnaris.com/api';

// Parse a growing SSE text buffer into complete events plus the leftover
// partial frame. Each event is a `data: <json>` line terminated by a blank
// line. Returns { events: [parsed objects], remainder: string } — feed the
// remainder back in with the next chunk. Malformed frames are skipped.
export function parseSSEBuffer(buffer) {
  const events = [];
  const frames = buffer.split('\n\n');
  const remainder = frames.pop() ?? ''; // last piece may be an incomplete frame
  for (const frame of frames) {
    const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
    if (!dataLine) continue;
    const payload = dataLine.slice(5).trim();
    if (!payload) continue;
    try {
      events.push(JSON.parse(payload));
    } catch {
      // ignore a malformed frame rather than aborting the stream
    }
  }
  return { events, remainder };
}

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.baseURL = API_BASE_URL;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  setRefreshToken(refreshToken) {
    this.refreshToken = refreshToken;
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }
  }

  onRefreshed(token) {
    this.refreshSubscribers.forEach(callback => callback(token));
    this.refreshSubscribers = [];
  }

  addRefreshSubscriber(callback) {
    this.refreshSubscribers.push(callback);
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: this.refreshToken })
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      this.setToken(data.access_token);
      return data.access_token;
    } catch (error) {
      // Refresh failed, clear tokens
      this.setToken(null);
      this.setRefreshToken(null);
      throw error;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...(options.headers || {})
      }
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
        // Token expired - try to refresh
        if (this.refreshToken && !this.isRefreshing) {
          this.isRefreshing = true;

          try {
            const newToken = await this.refreshAccessToken();
            this.isRefreshing = false;
            this.onRefreshed(newToken);

            // Retry the original request with new token
            config.headers['Authorization'] = `Bearer ${newToken}`;
            const retryResponse = await fetch(url, config);

            if (!retryResponse.ok) {
              const errorData = await retryResponse.json().catch(() => ({}));
              throw new Error(errorData.detail || `HTTP error! status: ${retryResponse.status}`);
            }

            return await retryResponse.json();
          } catch (refreshError) {
            this.isRefreshing = false;
            // Refresh failed - emit auth failed event
            window.dispatchEvent(new CustomEvent('authenticationFailed', {
              detail: { reason: 'token_expired' }
            }));
            throw new Error('Authentication failed');
          }
        } else if (this.isRefreshing) {
          // Another request is already refreshing, wait for it
          return new Promise((resolve, reject) => {
            this.addRefreshSubscriber(async (newToken) => {
              try {
                config.headers['Authorization'] = `Bearer ${newToken}`;
                const retryResponse = await fetch(url, config);

                if (!retryResponse.ok) {
                  const errorData = await retryResponse.json().catch(() => ({}));
                  reject(new Error(errorData.detail || `HTTP error! status: ${retryResponse.status}`));
                  return;
                }

                const data = await retryResponse.json();
                resolve(data);
              } catch (error) {
                reject(error);
              }
            });
          });
        } else {
          // No refresh token available
          window.dispatchEvent(new CustomEvent('authenticationFailed', {
            detail: { reason: 'token_expired' }
          }));
          throw new Error('Authentication failed');
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Handle 204 No Content responses (e.g., DELETE operations)
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // HTTP verb helper methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // Auth methods
  async login(username, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });

    if (response?.access_token) {
      this.setToken(response.access_token);
    }

    if (response?.refresh_token) {
      this.setRefreshToken(response.refresh_token);
    }

    return response;
  }

  async register(userData) {
    const response = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    if (response?.access_token) {
      this.setToken(response.access_token);
    }

    if (response?.refresh_token) {
      this.setRefreshToken(response.refresh_token);
    }

    return response;
  }

  async socialAuth(provider, token) {
    const response = await this.request('/auth/social', {
      method: 'POST',
      body: JSON.stringify({ provider, token }),
    });

    if (response?.access_token) {
      this.setToken(response.access_token);
    }

    if (response?.refresh_token) {
      this.setRefreshToken(response.refresh_token);
    }

    return response;
  }

  async getSocialAuthUrls(state = null) {
    const params = state ? `?state=${encodeURIComponent(state)}` : '';
    return this.request(`/auth/social/urls${params}`);
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async extendSession() {
    // Now uses refresh token automatically via the request method
    return this.refreshAccessToken();
  }

  async logout() {
    // Revoke refresh token on server
    if (this.refreshToken) {
      try {
        await this.request('/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refresh_token: this.refreshToken })
        });
      } catch (error) {
        console.error('Error revoking refresh token:', error);
      }
    }

    // Clear local tokens
    this.setToken(null);
    this.setRefreshToken(null);
  }

  // Dashboard methods
  async getDashboardStats() {
    return this.request('/finance/dashboard');
  }

  async getDashboardSummary() {
    return this.request('/dashboard/summary');
  }

  // Accounts management
  async getAccounts() {
    return this.request('/accounts');
  }

  async getAccount(accountId) {
    return this.request(`/accounts/${accountId}`);
  }

  async getAccountTransactions(accountId, params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        query.append(key, value);
      }
    });
    const queryString = query.toString();
    return this.request(`/accounts/${accountId}/transactions${queryString ? `?${queryString}` : ''}`);
  }

  async getAccountHoldings(accountId) {
    return this.request(`/accounts/${accountId}/holdings`);
  }

  async getAllTransactions(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        query.append(key, value);
      }
    });
    const queryString = query.toString();
    return this.request(`/transactions${queryString ? `?${queryString}` : ''}`);
  }

  async getTransaction(transactionId) {
    return this.request(`/transactions/${transactionId}`);
  }

  async createTransaction(transactionData) {
    return this.request('/transactions', {
      method: 'POST',
      body: JSON.stringify(transactionData)
    });
  }

  async updateTransaction(transactionId, updates) {
    return this.request(`/transactions/${transactionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  }

  async categorizeTransactionWithAi(description) {
    return this.request('/transactions/categorize-with-ai', {
      method: 'POST',
      body: JSON.stringify({ description })
    });
  }

  async deleteTransaction(transactionId) {
    return this.request(`/transactions/${transactionId}`, {
      method: 'DELETE'
    });
  }

  async getSimilarTransactions(accountId, description) {
    const params = new URLSearchParams({
      account_id: accountId,
      description: description
    });
    return this.request(`/transactions/similar?${params}`);
  }

  async bulkUpdateTransactionCategory(transactionIds, newCategory, categoryIcon) {
    return this.request('/transactions/bulk-update-category', {
      method: 'POST',
      body: JSON.stringify({
        transaction_ids: transactionIds,
        new_category: newCategory,
        category_icon: categoryIcon
      })
    });
  }

  async detectPotentialTransfers() {
    return this.request('/transactions/detect-transfers', {
      method: 'POST'
    });
  }

  async linkTransfer(transactionIds) {
    return this.request('/transactions/link-transfer', {
      method: 'POST',
      body: JSON.stringify({
        transaction_ids: transactionIds
      })
    });
  }

  async createAccount(accountData) {
    return this.request('/accounts', {
      method: 'POST',
      body: JSON.stringify(accountData),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async updateAccount(accountId, accountData) {
    return this.request(`/accounts/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify(accountData),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async deleteAccount(accountId) {
    return this.request(`/accounts/${accountId}`, {
      method: 'DELETE'
    });
  }

  // Statements management
  async getStatements(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        query.append(key, value);
      }
    });
    const queryString = query.toString();
    return this.request(`/statements${queryString ? `?${queryString}` : ''}`);
  }

  async getUpcomingStatements(daysAhead = 30) {
    return this.request(`/statements/upcoming?days_ahead=${daysAhead}`);
  }

  async getStatement(statementId) {
    return this.request(`/statements/${statementId}`);
  }

  async createStatement(statementData) {
    return this.request('/statements', {
      method: 'POST',
      body: JSON.stringify(statementData)
    });
  }

  async updateStatement(statementId, statementData) {
    return this.request(`/statements/${statementId}`, {
      method: 'PUT',
      body: JSON.stringify(statementData)
    });
  }

  async deleteStatement(statementId) {
    return this.request(`/statements/${statementId}`, {
      method: 'DELETE'
    });
  }

  async markStatementPaid(statementId, paymentDate = null, amountPaid = null) {
    const params = new URLSearchParams();
    if (paymentDate) params.append('payment_date', paymentDate);
    if (amountPaid) params.append('amount_paid', amountPaid);
    const queryString = params.toString();
    return this.request(`/statements/${statementId}/mark-paid${queryString ? `?${queryString}` : ''}`, {
      method: 'POST'
    });
  }

  async markStatementUnpaid(statementId) {
    return this.request(`/statements/${statementId}/mark-unpaid`, {
      method: 'POST'
    });
  }

  async recalculateStatementDueDates(accountId) {
    return this.request(`/statements/account/${accountId}/recalculate-due-dates`, {
      method: 'POST'
    });
  }

  async markTransactionAsStatementEnd(transactionId) {
    return this.request(`/transactions/${transactionId}/mark-statement-end`, {
      method: 'POST'
    });
  }

  // User management (keep from original)
  async getUsers() {
    return this.request('/users');
  }

  async getUser(id) {
    return this.request(`/users/${id}`);
  }

  async createUser(user) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(user)
    });
  }

  async updateUser(id, user) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(user)
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE'
    });
  }

  async changePassword(userId, passwordData) {
    return this.request(`/users/${userId}/change-password`, {
      method: 'POST',
      body: JSON.stringify(passwordData)
    });
  }

  async unlockUser(userId) {
    return this.request(`/users/${userId}/unlock`, {
      method: 'POST'
    });
  }

  // Settings methods (keep from original)
  async getSettings() {
    return this.request('/settings');
  }

  async updateSetting(key, value) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify({ key, value })
    });
  }

  // Transaction Import methods
  async analyzeTransactionFile(formData, progressCallback) {
    return this.requestWithProgress('/accounts/analyze-file', {
      method: 'POST',
      body: formData
    }, progressCallback);
  }

  async getTaskStatus(taskId) {
    return this.request(`/tasks/${taskId}/status`);
  }

  async importTransactions(accountId, data) {
    return this.request(`/accounts/${accountId}/import-transactions`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async analyzeAccountTransactions(accountId) {
    return this.request(`/accounts/${accountId}/analyze-transactions`, {
      method: 'POST'
    });
  }

  async analyzeAccountTransactionsWithConfig(accountId, config) {
    return this.request(`/accounts/${accountId}/analyze-transactions`, {
      method: 'POST',
      body: JSON.stringify({
        date_range: config.dateRange,
        categories: config.categories.length > 0 ? config.categories : null,
        question: config.question,
        analysis_type: config.analysisType || 'comprehensive'
      })
    });
  }

  async analyzeAllTransactionsWithConfig(config) {
    // Use dedicated endpoint for all accounts
    return this.request(`/all-accounts/analyze-transactions`, {
      method: 'POST',
      body: JSON.stringify({
        date_range: config.dateRange,
        categories: config.categories.length > 0 ? config.categories : null,
        question: config.question,
        analysis_type: config.analysisType || 'comprehensive'
      })
    });
  }

  async detectDuplicateTransactions(accountId) {
    return this.request(`/accounts/${accountId}/detect-duplicates`, {
      method: 'POST'
    });
  }

  async bulkDeleteTransactions(transactionIds) {
    return this.request('/transactions/_bulk', {
      method: 'DELETE',
      body: JSON.stringify({ transaction_ids: transactionIds })
    });
  }

  // Categories management
  async getCategories() {
    return this.request('/categories');
  }

  async getCategoryStats() {
    return this.request('/categories/stats');
  }

  async createCategory(categoryData) {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
  }

  async updateCategory(categoryId, categoryData) {
    return this.request(`/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    });
  }

  async deleteCategory(categoryId) {
    return this.request(`/categories/${categoryId}`, {
      method: 'DELETE'
    });
  }

  async checkTransactionDuplicates(accountId, transactions) {
    return this.request(`/accounts/${accountId}/check-duplicates`, {
      method: 'POST',
      body: JSON.stringify({ transactions })
    });
  }

  // Receipt scanning methods
  // Stream an AI transaction analysis via SSE. Calls onDelta(text) for each
  // markdown chunk and onSummary({transaction_count,total_amount}) at the end.
  // Resolves when the stream completes; throws on HTTP failure or a stream
  // `error` event so the caller can fall back to the job/poll path.
  async streamTransactionAnalysis({ accountId = null, config = {}, onDelta, onSummary } = {}) {
    const path = accountId
      ? `/accounts/${accountId}/analyze-transactions/stream`
      : `/all-accounts/analyze-transactions/stream`;
    const body = JSON.stringify({
      date_range: config.dateRange,
      categories: config.categories,
      question: config.question,
      analysis_type: config.analysisType || 'comprehensive',
    });
    const doFetch = () => fetch(`${this.baseURL}${path}`, {
      method: 'POST',
      headers: { ...this.getHeaders(), Accept: 'text/event-stream' },
      body,
    });

    let response = await doFetch();
    if (response.status === 401 && this.refreshToken) {
      await this.refreshAccessToken();
      response = await doFetch();
    }
    if (!response.ok || !response.body) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `AI analysis stream failed (HTTP ${response.status})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let streamError = null;
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, remainder } = parseSSEBuffer(buffer);
      buffer = remainder;
      for (const evt of events) {
        if (evt.type === 'delta') onDelta?.(evt.text || '');
        else if (evt.type === 'summary') onSummary?.(evt);
        else if (evt.type === 'error') streamError = new Error(evt.error || 'stream error');
        else if (evt.type === 'done') return;
      }
      if (streamError) throw streamError;
    }
    if (streamError) throw streamError;
  }

  async analyzeReceiptImage(formData, progressCallback) {
    return this.requestWithProgress('/accounts/analyze-receipt', {
      method: 'POST',
      body: formData
    }, progressCallback);
  }

  async importReceiptItems(accountId, data) {
    return this.request(`/accounts/${accountId}/import-receipt-items`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getReceipts(accountId) {
    return this.request(`/accounts/${accountId}/receipts`);
  }

  /**
   * Returns `{receipts, total, limit, offset, has_more}`.
   * All filter/sort is applied server-side so `total` always reflects the
   * filtered count — the client just renders what the server sent. Backend
   * caps limit at 200; default 50.
   */
  async getAllReceipts(filters = {}) {
    const params = new URLSearchParams();
    if (filters.accountId) params.append('account_id', filters.accountId);
    if (filters.unlinked !== undefined) params.append('unlinked', filters.unlinked);
    if (filters.search) params.append('search', filters.search);
    if (filters.importance && filters.importance !== 'all') {
      params.append('importance', filters.importance);
    }
    if (filters.tags && filters.tags.length > 0) {
      filters.tags.forEach(t => params.append('tags', t));
    }
    if (filters.sort) params.append('sort', filters.sort);
    if (filters.sortOrder) params.append('sort_order', filters.sortOrder);
    if (filters.limit !== undefined) params.append('limit', filters.limit);
    if (filters.offset !== undefined) params.append('offset', filters.offset);
    const queryString = params.toString();
    return this.request(`/receipts${queryString ? `?${queryString}` : ''}`);
  }

  async getReceipt(receiptId) {
    return this.request(`/receipts/${receiptId}`);
  }

  async updateReceipt(data) {
    return this.request('/receipts/update', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteReceipt(receiptId) {
    return this.request(`/receipts/${receiptId}`, {
      method: 'DELETE'
    });
  }

  async getReceiptVersions(receiptId) {
    return this.request(`/receipts/${receiptId}/versions`);
  }

  async restoreReceiptVersion(receiptId, versionNumber) {
    return this.request(`/receipts/${receiptId}/restore/${versionNumber}`, {
      method: 'POST'
    });
  }

  async getMatchingTransactions(receiptId, dateRange = 999999) {
    // Always fetch with wide date range (999999), let frontend filter
    return this.request(`/receipts/${receiptId}/matching-transactions?date_range=${dateRange}`);
  }

  async getMatchingReceipts(transactionId, dateRange = 999999) {
    // Always fetch with wide date range (999999), let frontend filter
    return this.request(`/transactions/${transactionId}/matching-receipts?date_range=${dateRange}`);
  }

  async linkReceiptToTransaction(receiptId, transactionId) {
    return this.request(`/receipts/${receiptId}/link-transaction/${transactionId}`, {
      method: 'POST'
    });
  }

  async unlinkReceipt(receiptId) {
    return this.request(`/receipts/${receiptId}/unlink`, {
      method: 'POST'
    });
  }

  async bulkMatchReceipts(receiptIds, minScore = 15, previewOnly = false) {
    return this.request('/receipts/bulk-match', {
      method: 'POST',
      body: JSON.stringify({
        receipt_ids: receiptIds,
        min_score: minScore,
        preview_only: previewOnly
      })
    });
  }

  async applyBulkMatches(matches) {
    return this.request('/receipts/apply-matches', {
      method: 'POST',
      body: JSON.stringify({
        matches: matches
      })
    });
  }

  // Helper for polling task status
  async pollForResult(statusEndpoint, maxAttempts = 30, pollInterval = 1000) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      try {
        const status = await this.request(statusEndpoint);
        if (status.status === 'completed') {
          return status;
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'Task failed');
        }
        // Still processing, continue polling
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling on network errors
      }
    }
    throw new Error('Request timed out');
  }

  // Dad jokes - now uses polling
  async getDadJoke() {
    // Start the task
    const startResponse = await this.request(`/dad-joke?t=${Date.now()}`);

    // Check if it's the new non-blocking format
    if (startResponse.task_id) {
      const result = await this.pollForResult(`/dad-joke-status/${startResponse.task_id}`);
      return { joke: result.joke };
    }

    // Legacy fallback
    return startResponse;
  }

  // Smart quotes - AI Quote Battle with 7 AIs
  // Uses longer timeout (60s) due to KEDA cold start + 7 parallel AI calls
  async getSmartQuote() {
    const startResponse = await this.request(`/smart-quote?t=${Date.now()}`);

    if (startResponse.task_id) {
      // 60 attempts at 1s = 60 second timeout (cold start can take 30s + battle ~20s)
      const result = await this.pollForResult(`/smart-quote-status/${startResponse.task_id}`, 60);
      // New format: battle with starter and responses
      if (result.battle) {
        return { battle: result.battle };
      }
      // Legacy format: single quote
      return { quote: result.quote };
    }

    return startResponse;
  }

  // Quote Battle Leaderboard (Redis-backed)
  async getQuoteBattleLeaderboard() {
    return this.request('/quote-battle/leaderboard');
  }

  async recordQuoteBattleVote(ai, perplexityPick = null) {
    return this.request('/quote-battle/vote', {
      method: 'POST',
      body: JSON.stringify({ ai, perplexity_pick: perplexityPick })
    });
  }

  // Chicken pictures - now uses polling
  async getChickenPicture() {
    const startResponse = await this.request(`/chicken-picture?t=${Date.now()}`);

    if (startResponse.task_id) {
      const result = await this.pollForResult(`/chicken-picture-status/${startResponse.task_id}`);
      return { url: result.url, width: result.width, height: result.height };
    }

    return startResponse;
  }

  // Cat pictures
  async getCatPicture() {
    // Add cache buster to prevent browser caching
    return this.request(`/cat-picture?t=${Date.now()}`);
  }

  // User settings
  async getUserSettings() {
    return this.request('/user/settings');
  }

  async updateUserSettings(settings) {
    return this.request('/user/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  // Helper method for progress tracking
  async requestWithProgress(endpoint, options = {}, progressCallback) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && progressCallback) {
          const progress = (event.loaded / event.total) * 100;
          progressCallback(progress);
        }
      });

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            resolve(xhr.responseText);
          }
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));

      xhr.open(options.method || 'GET', `${this.baseURL}${endpoint}`);

      const token = localStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      // Don't set Content-Type for FormData, let browser set it with boundary
      if (!(options.body instanceof FormData)) {
        xhr.setRequestHeader('Content-Type', 'application/json');
      }

      xhr.send(options.body);
    });
  }

  // ============================================================================
  // WELLNESS API METHODS (FIGARO)
  // ============================================================================

  // Wellness Dashboard
  async getWellnessDashboard() {
    return this.request('/wellness/dashboard');
  }

  // Wellness Goals
  async getWellnessGoals(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/wellness/goals${queryString ? '?' + queryString : ''}`);
  }

  async createWellnessGoal(goalData) {
    return this.request('/wellness/goals', {
      method: 'POST',
      body: JSON.stringify(goalData)
    });
  }

  async updateWellnessGoal(goalId, updates) {
    return this.request(`/wellness/goals/${goalId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  async deleteWellnessGoal(goalId) {
    return this.request(`/wellness/goals/${goalId}`, {
      method: 'DELETE'
    });
  }

  // Wellness Logs
  async getWellnessLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/wellness/logs${queryString ? '?' + queryString : ''}`);
  }

  async createWellnessLog(logData) {
    return this.request('/wellness/logs', {
      method: 'POST',
      body: JSON.stringify(logData)
    });
  }

  // Bewellandfree Scores
  async getWellnessScores(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/wellness/scores${queryString ? '?' + queryString : ''}`);
  }

  async getLatestWellnessScore() {
    return this.request('/wellness/scores/latest');
  }

  async createWellnessScore(scoreData) {
    return this.request('/wellness/scores', {
      method: 'POST',
      body: JSON.stringify(scoreData)
    });
  }

  // Wellness Profile
  async getWellnessProfile() {
    return this.request('/wellness/profile');
  }

  async updateWellnessProfile(updates) {
    return this.request('/wellness/profile', {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  }

  // Milestones
  async getWellnessMilestones(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/wellness/milestones${queryString ? '?' + queryString : ''}`);
  }

  async celebrateMilestone(milestoneId) {
    return this.request(`/wellness/milestones/${milestoneId}/celebrate`, {
      method: 'POST'
    });
  }

  // Analytics
  async getWellnessStreaks() {
    return this.request('/wellness/streaks');
  }

  async getMoneySaved() {
    return this.request('/wellness/money-saved');
  }

  // ==================== Recurring Payments ====================

  async getRecurringPayments(activeOnly = true) {
    return this.request(`/recurring-payments?active_only=${activeOnly}`);
  }

  async getRecurrenceTypes() {
    return this.request('/recurring-payments/recurrence-types');
  }

  async detectRecurringPatterns(minOccurrences = 3, monthsBack = 6) {
    return this.request(`/recurring-payments/detect-patterns?min_occurrences=${minOccurrences}&months_back=${monthsBack}`);
  }

  async searchTransactionsForPayment(paymentId, search = '') {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/recurring-payments/${paymentId}/search-transactions${params}`);
  }

  async bulkLinkTransactions(paymentId, transactionIds) {
    return this.request(`/recurring-payments/${paymentId}/bulk-link`, {
      method: 'POST',
      body: JSON.stringify({ transaction_ids: transactionIds })
    });
  }

  async bulkUnlinkTransactions(paymentId, transactionIds) {
    return this.request(`/recurring-payments/${paymentId}/bulk-unlink`, {
      method: 'POST',
      body: JSON.stringify({ transaction_ids: transactionIds })
    });
  }

  async getRecurringPayment(paymentId) {
    return this.request(`/recurring-payments/${paymentId}`);
  }

  async createRecurringPayment(data) {
    return this.request('/recurring-payments', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateRecurringPayment(paymentId, data) {
    return this.request(`/recurring-payments/${paymentId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteRecurringPayment(paymentId) {
    return this.request(`/recurring-payments/${paymentId}`, {
      method: 'DELETE'
    });
  }

  async createRecurringPaymentFromTransaction(transactionId, recurrenceType) {
    return this.request(`/recurring-payments/create-from-transaction/${transactionId}?recurrence_type=${recurrenceType}`, {
      method: 'POST'
    });
  }

  async linkTransactionToRecurring(paymentId, transactionId) {
    return this.request(`/recurring-payments/${paymentId}/link-transaction/${transactionId}`, {
      method: 'POST'
    });
  }

  async unlinkTransactionFromRecurring(paymentId, transactionId) {
    return this.request(`/recurring-payments/${paymentId}/unlink-transaction/${transactionId}`, {
      method: 'DELETE'
    });
  }
}

const apiService = new ApiService();
export default apiService;
